import React, { useState } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Button,
  Badge,
  Tooltip,
  CircularProgress,
  SvgIcon,
  useTheme as useMuiTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Palette as PaletteIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  BarChart as BarChartIcon,
  BugReport as BugReportIcon,
  WarningAmber as WarningAmberIcon,
  HourglassBottomRounded as HourglassIcon,
  SchoolOutlined as SchoolIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminBadge from '../common/AdminBadge';
import { useAuth } from '../../context/AuthContext';
import UserAvatar from '../ui/UserAvatar';
import ThemeToggle from '../ThemeToggle';
import ThemeSelector from '../ui/ThemeSelector';
import LanguageSelector from '../LanguageSelector';
import { useSidebar } from '../../context/SidebarContext';
import { useOrg } from '../../context/OrgContext';
import { useBillingOptional } from '../../context/BillingContext';
import { goToLimitAction, limitActionOf, limitButton } from '../billing/limitAction';
import { Z_INDEX } from '../../constants/zIndex';
import authService from '../../services/authService';
import { canAccessApp } from '../../utils/userAccess';
import {
  choosePlanButtonSx, daysLeftFraction, daysLeftTitle, freeAccessTone, softToneColor,
} from '../billing/freeAccessNotice';

// Separación entre el avatar y el anillo de días de una prueba o un comp.
const RING_GAP = 4;

// El triángulo del plan agotado (#1300), blanco por dentro para que se lea sobre cualquier avatar
// y sin círculo alrededor. El icono de MUI es solo el contorno: el relleno no se le puede dar.
const DangerTriangleIcon = ({ stroke, fill, ...props }) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path d="M12 3.5 21.5 20h-19z" fill={fill} stroke={stroke} strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 9.5V14" fill="none" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="12" cy="17" r="1.25" fill={stroke} />
  </SvgIcon>
);

/**
 * UserMenu Component
 *
 * Avatar with dropdown menu containing:
 * - User info (name/email)
 * - Theme toggle
 * - Settings link
 * - Logout button
 *
 * Used in both expanded Sidebar and MobileHeader
 *
 * Props:
 * - onLogoutClick: optional handler called INSTEAD of the default logout flow.
 *   Useful for contexts that need to intercept logout (e.g. onboarding steps
 *   that want to show a confirmation dialog). When omitted, the default flow
 *   runs `authService.logout()` and navigates to `/login`.
 */
const UserMenu = ({ size = 32, showName = false, compact = false, onLogoutClick, hideSettings = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const { userProfile, user } = useAuth();
  const { currentOrg } = useOrg();
  const { isMobile, closeOverlay } = useSidebar();
  // Opcional a propósito: este menú también sale en pantallas previas al alta,
  // donde el árbol de facturación no está montado. Sin datos no hay aviso.
  const billing = useBillingOptional();
  const isExhausted = Boolean(billing?.isExhausted);
  const openLimitNotice = billing?.openLimitNotice;
  // #759: la salida de un plan de pago agotado, la misma que el aviso (subir de plan, avisar al admin…).
  const limitAction = limitActionOf(billing?.usage);
  // #759 D18: la prueba o el comp llevan un anillo alrededor del avatar que se vacía con los días,
  // ámbar mientras queda tiempo y rojo en sus días de aviso. Al abrir el menú, cuántos quedan y
  // el botón para elegir plan.
  const freeAccess = billing?.usage?.freeAccess ?? null;
  const freeAccessColor = freeAccess ? softToneColor(muiTheme, freeAccessTone(freeAccess)) : null;
  const freeAccessSummary = freeAccess
    ? `${daysLeftTitle(t, freeAccess.daysLeft)} · ${t('billing.freeAccess.balanceUsed', {
      pct: Math.round(Number(billing.usage.percentUsed ?? 0)),
      defaultValue: '{{pct}} % del saldo usado',
    })}`
    : null;

  const [anchorEl, setAnchorEl] = useState(null);
  const [themeSelectorOpen, setThemeSelectorOpen] = useState(false);
  // Track pending navigation to execute AFTER menu exit animation completes
  // This prevents MUI Menu backdrop from becoming orphaned and blocking UI
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAnalyticsClick = () => {
    setPendingNavigation('/analytics');
    handleClose();
  };

  // Academia AI Flock: guías y vídeos de uso en la web pública (pestaña nueva).
  const ACADEMY_URL = 'https://aiflock.ai/academia';
  const handleAcademyClick = () => {
    window.open(ACADEMY_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const handleMyReportsClick = () => {
    setPendingNavigation('/my-reports');
    handleClose();
  };

  const handleSettingsClick = () => {
    // Defer navigation until menu exit animation completes
    // This prevents the MUI Menu backdrop from becoming orphaned
    setPendingNavigation('/settings');
    handleClose();
    // Navigation happens in handleMenuExited
  };

  const handleAdminClick = () => {
    setPendingNavigation('/admin');
    handleClose();
  };

  const handleChoosePlanClick = () => {
    setPendingNavigation('/billing/required');
    handleClose();
  };

  const handleOpenThemeSelector = () => {
    handleClose();
    setThemeSelectorOpen(true);
  };

  const handleLogout = () => {
    // If a parent wants to intercept logout (e.g. to show a confirmation),
    // close the menu and delegate — parent is responsible for the rest.
    if (onLogoutClick) {
      handleClose();
      // Defer to next tick so the menu can start its exit animation before
      // any parent-rendered dialog opens; avoids MUI backdrop stacking glitches.
      setTimeout(() => onLogoutClick(), 0);
      return;
    }
    // Defer navigation until menu exit animation completes
    setPendingNavigation('/logout');
    handleClose();
    // Logout and navigation happen in handleMenuExited
  };

  /**
   * Handler called when Menu exit animation completes.
   * Executes any pending navigation to avoid race conditions with MUI backdrop.
   */
  const handleMenuExited = () => {
    if (pendingNavigation) {
      if (pendingNavigation === '/logout') {
        authService.logout();
        navigate('/login');
      } else {
        navigate(pendingNavigation);
      }
      if (isMobile) {
        closeOverlay();
      }
      setPendingNavigation(null);
    }
  };

  // Get display name
  const displayName = userProfile?.display_name ||
                      userProfile?.associated_person ||
                      userProfile?.email ||
                      user?.email ||
                      t('user.defaultName', 'Usuario');

  // Get email for subtitle
  const email = userProfile?.email || user?.email;

  return (
    <>
      <Box
        onClick={handleClick}
        data-testid="user-menu-button"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          padding: compact ? '6px' : '8px 12px',
          borderRadius: 2,
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: muiTheme.palette.action.hover,
          },
        }}
      >
        {/* El triángulo es el aviso PERMANENTE de plan agotado (#1300): mientras
            el plan esté al límite está siempre a la vista, sin depender de que el
            modal salte ni de que el usuario no lo haya cerrado. La acción vive en
            la primera entrada del menú, justo debajo. */}
        <Tooltip title={freeAccessSummary ?? ''}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            {freeAccess && (
              <>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={size + RING_GAP * 2}
                  thickness={2.5}
                  sx={{ position: 'absolute', top: -RING_GAP, left: -RING_GAP, color: alpha(freeAccessColor, 0.25) }}
                />
                <CircularProgress
                  data-testid="free-access-ring"
                  variant="determinate"
                  value={daysLeftFraction(freeAccess) * 100}
                  size={size + RING_GAP * 2}
                  thickness={2.5}
                  sx={{
                    position: 'absolute',
                    top: -RING_GAP,
                    left: -RING_GAP,
                    color: freeAccessColor,
                    '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
                  }}
                />
              </>
            )}
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              invisible={!isExhausted}
              badgeContent={
                <Tooltip title={t('billing.limit.tooltip', 'Has agotado el límite de tu plan')}>
                  <DangerTriangleIcon
                    data-testid="usage-exhausted-badge"
                    stroke={softToneColor(muiTheme, 'error')}
                    fill={muiTheme.palette.common.white}
                    sx={{ fontSize: 16 }}
                  />
                </Tooltip>
              }
            >
              <UserAvatar size={size} />
            </Badge>
          </Box>
        </Tooltip>
        {showName && !compact && (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 160,
              }}
            >
              {displayName}
            </Typography>
            {freeAccessSummary && (
              <Typography variant="caption" color="text.secondary" data-testid="free-access-summary" sx={{ display: 'block' }}>
                {freeAccessSummary}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // REMOVED: onClick={handleClose} - redundant since MenuItems handle their own close
        // This was causing issues with theme toggle clicks
        TransitionProps={{
          onExited: handleMenuExited, // Execute navigation AFTER exit animation completes
        }}
        PaperProps={{
          elevation: 4,
          sx: {
            minWidth: 220,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: 1,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          zIndex: Z_INDEX.USER_MENU_DROPDOWN,
        }}
      >
        {/* User Info Section */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {displayName}
          </Typography>
          {email && email !== displayName && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
              {email}
            </Typography>
          )}
          {/* #759 D18: el aviso flotante se cierra; esto no. Es la vía permanente a elegir plan,
              justo debajo de quién eres, como lo que es: el estado de tu cuenta. */}
          {freeAccess && (
            <Box data-testid="free-access-menu-status" sx={{ mt: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <HourglassIcon sx={{ fontSize: 18, color: freeAccessColor }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {isExhausted
                    ? t('billing.freeAccess.balanceExhausted', 'Has agotado el saldo de tu prueba')
                    : daysLeftTitle(t, freeAccess.daysLeft)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                disableElevation
                onClick={handleChoosePlanClick}
                sx={{ mt: 1, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, ...choosePlanButtonSx(muiTheme) }}
              >
                {t('billing.freeAccess.choosePlan', 'Elegir un plan')}
              </Button>
            </Box>
          )}
        </Box>

        {/* Segunda vía del aviso permanente (#1300): el triángulo del avatar se
            ve, pero no se puede pulsar por separado —el clic abre este menú—, así
            que la ACCIÓN vive aquí. Sin ella, pedir más uso seguiría dependiendo
            de que el modal apareciese solo. */}
        {/* Una prueba o un comp no piden más uso: eligen plan, desde el bloque de arriba. */}
        {isExhausted && !freeAccess && (
          <MenuItem
            data-testid="usage-exhausted-menuitem"
            onClick={() => { handleClose(); goToLimitAction(limitAction, { navigate, openLimitNotice }); }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <WarningAmberIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            {/* El color va en los Typography y no en el MenuItem: `ListItemText`
                aplica su propia tipografía (`text.primary` / `text.secondary`) y
                pisa el color heredado, así que el texto salía en negro con el
                icono en rojo. */}
            <ListItemText
              primary={t('billing.limit.menuTitle', 'Has agotado tu plan')}
              secondary={limitButton(t, limitAction)}
              primaryTypographyProps={{ fontWeight: 700, color: 'error.main' }}
              secondaryTypographyProps={{ color: 'error.main' }}
            />
          </MenuItem>
        )}

        <Divider />

        {/* Theme Toggle */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography variant="body2">{t('settings.theme', 'Tema')}</Typography>
          <ThemeToggle />
        </Box>

        {/* Language Selector */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography variant="body2">{t('settings.language', 'Idioma')}</Typography>
          <LanguageSelector />
        </Box>

        {/* Theme Selector */}
        <MenuItem onClick={handleOpenThemeSelector}>
          <ListItemIcon>
            <PaletteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('settings.changeTheme', 'Cambiar tema')}</ListItemText>
        </MenuItem>

        <Divider />

        {/* Admin Panel — solo visible para rol admin */}
        {userProfile?.role === 'admin' && (
          <MenuItem
            onClick={handleAdminClick}
            sx={{
              color: 'error.main',
              fontWeight: 700,
              '& .MuiListItemIcon-root': { color: 'error.main' },
              '&:hover': {
                bgcolor: 'error.main',
                color: 'error.contrastText',
                '& .MuiListItemIcon-root': { color: 'error.contrastText' },
              },
            }}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 700 }}>
              Panel de Admin
            </ListItemText>
            <AdminBadge userProfile={userProfile} />
          </MenuItem>
        )}

        {/* Analytics — visible a flock admins (role='admin') y org owners/admins. */}
        {canAccessApp(userProfile) && (
          userProfile?.role === 'admin' ||
          currentOrg?.member_role === 'owner' ||
          currentOrg?.member_role === 'admin'
        ) && (
          <MenuItem onClick={handleAnalyticsClick}>
            <ListItemIcon>
              <BarChartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('tabAnalytics', 'Analytics')}</ListItemText>
            {userProfile?.role === 'admin' && <AdminBadge userProfile={userProfile} />}
          </MenuItem>
        )}

        {/* Settings — hidden when:
              (a) user lacks app access (no terms accepted), OR
              (b) parent explicitly opts out via hideSettings (e.g. blocking
                  pages like BillingRequiredPage where /settings would loop
                  back due to OnboardingGuard's billing gate).
            Clicking it from a blocking page briefly leaks the MainLayout
            chrome during OnboardingGuard's async billing check, so we don't
            offer the option in the first place. */}
        {/* Academia — guías y vídeos de uso de Flock en aiflock.ai/academia.
            Es contenido público, así que se muestra a cualquier usuario. */}
        <MenuItem onClick={handleAcademyClick}>
          <ListItemIcon>
            <SchoolIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('tabAcademy', 'Academia')}</ListItemText>
        </MenuItem>

        {/* Mis incidencias — seguimiento de los reportes que ha enviado el
            usuario (plan #63). Visible para cualquier usuario con acceso. */}
        {canAccessApp(userProfile) && (
          <MenuItem onClick={handleMyReportsClick}>
            <ListItemIcon>
              <BugReportIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('tabMyReports', 'Mis incidencias')}</ListItemText>
          </MenuItem>
        )}

        {!hideSettings && canAccessApp(userProfile) && (
          <MenuItem onClick={handleSettingsClick}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('tabSettings', 'Settings')}</ListItemText>
          </MenuItem>
        )}

        {/* Logout */}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('btnLogout', 'Logout')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Theme Selector Dialog */}
      <ThemeSelector
        open={themeSelectorOpen}
        onClose={() => setThemeSelectorOpen(false)}
      />
    </>
  );
};

export default UserMenu;
